// Utilitários compartilhados para transformar treinos da biblioteca (texto fiel
// à ficha original da Carol) nos campos estruturados usados no formulário de
// treino e na tela de execução da aluna.

export const GRUPO_POR_TEMPLATE = {
  lib_3030_a: 'Quadríceps', lib_3030_b: 'Ombros', lib_3030_c: 'Posteriores', lib_3030_d: 'Costas', lib_3030_e: 'Glúteos', lib_3030_abs: 'Core',
  lib_mod_a: 'Quadríceps', lib_mod_b: 'Costas', lib_mod_c: 'Posteriores', lib_mod_d: 'Peito', lib_mod_e: 'Glúteos', lib_mod_abs_pant: 'Core',
  lib_def4_a: 'Quadríceps', lib_def4_b: 'Peito e ombros', lib_def4_c: 'Costas, tríceps e bíceps', lib_def4_d: 'Posteriores e glúteos', lib_def4_e: 'Peito, ombros e braços', lib_def4_abs_pant: 'Core',
};

// Converte o formato "cru" da biblioteca (texto fiel à ficha original da Carol)
// para os campos estruturados do formulário (série/execução/descanso numéricos).
// Nada do texto original se perde: o que não encaixa nos campos numéricos vai para `obs`.
export function converterExercicioBiblioteca(raw, idx) {
  const seriesTxt = String(raw.series || '').trim();
  let modo = 'reps';
  let series = '1';
  let execucaoSeg = '';
  let reps = '';
  let descansoNum = null;
  const obsExtra = [];

  const descansoMatch = String(raw.descanso || '').trim().match(/^(?:Até\s*)?(\d+)/i);
  if (descansoMatch) descansoNum = parseInt(descansoMatch[1], 10);

  let m = seriesTxt.match(/^(\d+)\s*x\s*(\d+)"?\s*pausa\s*(\d+)"?$/i);
  if (m) {
    modo = 'tempo';
    series = m[1]; execucaoSeg = m[2]; descansoNum = parseInt(m[3], 10);
  } else if ((m = seriesTxt.match(/^(\d+)\s*x\s*(\d+)\s*segundos?$/i))) {
    modo = 'tempo';
    series = m[1]; execucaoSeg = m[2];
    if (descansoNum == null) descansoNum = 30;
  } else if ((m = seriesTxt.match(/^(\d+)\s*x\s*([\d/+]+)$/i))) {
    modo = 'reps';
    series = m[1]; reps = m[2];
  } else {
    modo = 'reps';
    const lead = seriesTxt.match(/^(\d+)/);
    series = lead ? lead[1] : '1';
    // Evita rótulos duplicados como "5x 5x até a falha".
    reps = lead ? seriesTxt.replace(/^\d+\s*x\s*/i, '') : seriesTxt;
  }

  if (descansoNum == null) {
    if (raw.descanso && raw.descanso !== '—') obsExtra.push(`Descanso: ${raw.descanso}`);
    descansoNum = '';
  }

  let posicaoPes = '';
  let posicaoMaos = '';
  const pos = (raw.posicionamento || '').trim();
  if (pos && pos !== '—') {
    if (/mã|pegada/i.test(pos)) posicaoMaos = pos;
    else if (/p[eé]/i.test(pos)) posicaoPes = pos;
    else obsExtra.push(`Posição: ${pos}`);
  }

  let obs = raw.execucao && raw.execucao !== '—' ? raw.execucao : '';
  if (obsExtra.length) obs = obs ? `${obs} · ${obsExtra.join(' · ')}` : obsExtra.join(' · ');

  return {
    id: `ex_${Date.now()}_${idx}_${Math.floor(Math.random() * 1e6)}`,
    nome: raw.nome,
    grupo: '',
    modo,
    series: String(series),
    execucaoSeg: modo === 'tempo' ? String(execucaoSeg || 30) : '',
    descansaSeg: descansoNum !== '' ? String(descansoNum) : '',
    reps: modo === 'reps' ? reps : '',
    carga: raw.carga && raw.carga !== '—' ? raw.carga : '',
    obs,
    posicaoPes,
    posicaoMaos,
    video: '',
    grupoCombinado: raw.grupoCombinado || '',
    ordemCombinada: raw.ordemCombinada || null,
    tipoCombinacao: raw.tipoCombinacao || '',
  };
}

// Compatibilidade com fichas do protocolo que já haviam sido atribuídas antes
// de os bi-sets ganharem campos estruturados.
const COMBINACOES_CONHECIDAS = {
  'Afundo': ['def4-a-afundo-agachamento', 1, 'Bi-set'],
  'Agachamento livre': ['def4-a-afundo-agachamento', 2, 'Bi-set'],
  'Supino vertical máquina — pegada pronada': ['def4-b-supino-vertical', 1, 'Bi-set'],
  'Supino vertical máquina — pegada neutra': ['def4-b-supino-vertical', 2, 'Bi-set'],
  'Elevação lateral com halteres': ['def4-b-elevacoes', 1, 'Bi-set'],
  'Elevação frontal com halteres': ['def4-b-elevacoes', 2, 'Bi-set'],
  'Desenvolvimento lateral máquina': ['def4-b-desenvolvimentos', 1, 'Bi-set'],
  'Desenvolvimento frontal máquina': ['def4-b-desenvolvimentos', 2, 'Bi-set'],
  'Passada': ['def4-d-passada-sumo', 1, 'Bi-set'],
  'Agachamento sumô': ['def4-d-passada-sumo', 2, 'Bi-set'],
};

export function dadosCombinacao(ex) {
  if (ex?.grupoCombinado) return {
    id: ex.grupoCombinado,
    ordem: Number(ex.ordemCombinada) || 1,
    tipo: ex.tipoCombinacao || 'Bi-set',
  };
  const legado = COMBINACOES_CONHECIDAS[ex?.nome];
  return legado ? { id: legado[0], ordem: legado[1], tipo: legado[2] } : null;
}

// Para treinos principais, complementa com o bloco fixo de ABS/panturrilha do protocolo
// (o método da Carol trata isso como parte do mesmo treino do dia, não uma ficha à parte).
export function extrasDoBloco(treino, treinosBiblioteca) {
  if (treino.categoria !== 'principal') return [];
  if (treino.protocolo === '30x30') {
    const bloco = treinosBiblioteca.find(t => t.id === 'lib_3030_abs');
    return bloco ? bloco.exercicios : [];
  }
  if (treino.protocolo === 'moderado') {
    const bloco = treinosBiblioteca.find(t => t.id === 'lib_mod_abs_pant');
    if (!bloco) return [];
    if (['A', 'C', 'E'].includes(treino.letra)) return bloco.exercicios.filter(e => /abdomin/i.test(e.nome));
    if (['B', 'D'].includes(treino.letra)) return bloco.exercicios.filter(e => /panturrilha/i.test(e.nome));
  }
  if (treino.protocolo === 'definicao4s') {
    const bloco = treinosBiblioteca.find(t => t.id === 'lib_def4_abs_pant');
    if (!bloco) return [];
    return ['A', 'C', 'E'].includes(treino.letra) ? bloco.exercicios : [];
  }
  return [];
}

// Monta a lista completa de exercícios (formato do formulário) para um treino da
// biblioteca, já somando o bloco extra correspondente.
export function montarExerciciosDoTreino(treino, treinosBiblioteca) {
  const brutos = [...treino.exercicios, ...extrasDoBloco(treino, treinosBiblioteca)];
  return brutos.map((ex, i) => converterExercicioBiblioteca(ex, i));
}
